import api from "../api.js";

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );

const pad2 = (value) => String(value).padStart(2, "0");
const formatLocalDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const toIsoDate = (value) => {
  if (!value) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatLocalDate(value);
  }
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  return formatLocalDate(parsed);
};

const fallbackDate = (offsetDays) => {
  const base = new Date();
  base.setDate(base.getDate() + offsetDays);
  return formatLocalDate(base);
};

const toSafeInt = (value, fallback, min = 0) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.max(min, Math.floor(num));
};

export const getCities = async () => {
  const { data } = await api.get("/providers/elivaas/cities");
  return data?.data || data || [];
};

export const searchRates = async (params = {}) => {
  const normalized = cleanParams({
    ...params,
    city: String(params.city || "").split(",")[0].trim() || undefined,
    checkinDate: toIsoDate(params.checkinDate || params.checkIn) || fallbackDate(7),
    checkoutDate: toIsoDate(params.checkoutDate || params.checkOut) || fallbackDate(10),
    adults: toSafeInt(params.adults, 2, 1),
    children: toSafeInt(params.children, 0, 0),
    bedroom: toSafeInt(params.bedroom || params.bedrooms || params.beds, undefined, 1),
    quantity: toSafeInt(params.quantity || params.rooms, undefined, 1),
    page: toSafeInt(params.page, 1, 1),
    pageSize: toSafeInt(params.pageSize, 12, 1),
  });

  try {
    const { data } = await api.get("/providers/elivaas/rates", {
      params: normalized,
    });
    return data || {};
  } catch (error) {
    const status = Number(error?.response?.status || 0);
    const responseMessage = String(
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      error?.message ||
      ""
    ).toLowerCase();
    if (status === 400 || responseMessage.includes("bad request")) {
      const retryParams = cleanParams({
        ...normalized,
        city: undefined,
        keyword: undefined,
        pageSize: Math.min(20, Number(normalized.pageSize || 12)),
      });
      try {
        const { data } = await api.get("/providers/elivaas/rates", {
          params: retryParams,
        });
        return data || {};
      } catch (_retryError) {
        return { list: [], pagination: { page: 1, pageSize: retryParams.pageSize || 12, total: 0, pages: 1 } };
      }
    }
    if (
      status === 503 ||
      responseMessage.includes("integration is disabled") ||
      responseMessage.includes("credentials are missing")
    ) {
      return { list: [], pagination: { page: 1, pageSize: normalized.pageSize || 12, total: 0, pages: 1 } };
    }
    return { list: [], pagination: { page: 1, pageSize: normalized.pageSize || 12, total: 0, pages: 1 } };
  }
};

export const getInventories = async (propertyId, { fresh = false, fromDate, toDate } = {}) => {
  const params = cleanParams({
    ...(fresh ? { fresh: 1 } : {}),
    fromDate: toIsoDate(fromDate) || fallbackDate(7),
    toDate: toIsoDate(toDate) || toIsoDate(fromDate) || fallbackDate(10),
  });
  const { data } = await api.get(`/providers/elivaas/properties/${propertyId}/inventories`, {
    params,
  });
  return data?.data || data || {};
};

export const createBooking = async (payload = {}) => {
  const { data } = await api.post("/providers/elivaas/bookings", payload);
  return data || {};
};

export const getPropertyReviews = async (propertyId) => {
  const { data } = await api.get(`/providers/elivaas/properties/${encodeURIComponent(propertyId)}/reviews`);
  return data?.data || data || null;
};

export const getRatePlans = async (propertyId) => {
  const { data } = await api.get(`/providers/elivaas/properties/${encodeURIComponent(propertyId)}/rate-plans`);
  return data?.data || data || [];
};

export const getPropertyQuotes = async (propertyId, params = {}) => {
  const { data } = await api.get(`/providers/elivaas/properties/${encodeURIComponent(propertyId)}/quotes`, {
    params: cleanParams({
      ...params,
      checkinDate: toIsoDate(params.checkinDate || params.checkIn),
      checkoutDate: toIsoDate(params.checkoutDate || params.checkOut),
      adults: toSafeInt(params.adults, 2, 1),
      children: toSafeInt(params.children, 0, 0),
      quantity: toSafeInt(params.quantity, 1, 1),
    }),
  });
  return data?.data || data || [];
};
